import { useCallback } from 'react';
import { Play as PlayIcon } from '@vgno/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { LinkButton } from '../LinkButton/LinkButton';
import { getPath, routeNames } from '../../shared/helpers/routes';
import {
  isDocumentary,
  isTVShowEpisode,
} from '../../shared/helpers/assetHelpers';
import { useAssetWatchtimeData } from '../utils/useAssetWatchtimeData';
import { useAssetRelease } from '../utils/useAssetRelease';
import { PromoBadge } from '../PromoBadge/PromoBadge';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC, MouseEventHandler, ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button';

import * as styles from './AssetButton.module.scss';

type AssetButtonProps = Pick<
  ButtonProps,
  'variant' | 'size' | 'rounded' | 'className'
> & {
  asset: SvpAssetAdditional;
  onClick?: (
    event: Parameters<MouseEventHandler>[0],
    asset: SvpAssetAdditional
  ) => void;
  onEpisodeButtonClick?: () => void;
  label?: ReactNode;
  url?: string;
};

const getPromoVariant = (asset: SvpAssetAdditional) => {
  if (isTVShowEpisode(asset)) {
    if (asset.series.episodeNumber === 1) {
      return asset.series.seasonNumber === 1 ? 'series' : 'season';
    }

    return 'episode';
  }

  if (isDocumentary(asset)) {
    return 'documentary';
  }

  return undefined;
};

export const AssetButton: FC<AssetButtonProps> = ({
  asset,
  onClick,
  onEpisodeButtonClick,
  label: labelProp,
  url: urlProp,
  className,
  size = 'large',
  variant = 'contained',
  rounded,
}) => {
  const { t } = useTranslation();
  const { position } = useAssetWatchtimeData(asset.id, asset.provider);
  const { isNewlyReleased } = useAssetRelease(asset);

  const promoVariant = isNewlyReleased ? getPromoVariant(asset) : undefined;

  const label = labelProp || `${t('Click to watch')}!`;
  const url =
    urlProp ||
    getPath(routeNames.asset, {
      asset,
      time: position,
    });

  const handleButtonClick: MouseEventHandler = useCallback(
    (event) => {
      if (onEpisodeButtonClick) {
        onEpisodeButtonClick();
      }
      if (onClick && asset) {
        onClick(event, asset);
      }
    },
    [onClick, asset]
  );

  return (
    <LinkButton
      size={size}
      variant={variant}
      rounded={rounded}
      isActiveBrandColor
      onClick={handleButtonClick}
      url={url}
      className={classNames(styles.button, className)}
    >
      <PlayIcon />
      {label}
      {promoVariant && (
        <div className={styles.badge}>
          <PromoBadge variant={promoVariant} />
        </div>
      )}
    </LinkButton>
  );
};
